import React from "react";
import {
  PictureOutlined,
  PlayCircleOutlined,
  LinkOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import type { MessageTemplate } from "@api/services/facebook";
import styles from "./styles.module.less";
import { ROUTES } from "@config/routes.config";
import { useNavigate } from "react-router-dom";

interface FacebookButton {
  type: "web_url" | "postback";
  title: string;
  url?: string;
  payload?: string;
}

interface TemplatePayload {
  title?: string;
  subtitle?: string;
  text?: string;
  image_url?: string;
  imageUrl?: string;
  media_type?: string;
  media_url?: string;
  mediaUrl?: string;
  aspect_ratio?: string;
  buttons?: FacebookButton[];
  elements?: Array<{
    title?: string;
    subtitle?: string;
    image_url?: string;
    imageUrl?: string;
    buttons?: FacebookButton[];
  }>;
}

interface TemplatePreviewProps {
  template: MessageTemplate;
  onSend?: () => void;
  onCancel?: () => void;
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  template,
  onSend,
  onCancel,
}) => {
  const navigate = useNavigate();
  if (!template || !template.payload) {
    return null;
  }

  const handleSend = () => {
    if (onSend) {
      onSend();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  let payload: TemplatePayload;
  try {
    payload =
      typeof template.payload === "string"
        ? JSON.parse(template.payload)
        : template.payload;
  } catch {
    return null;
  }

  const templateType = template.type?.toUpperCase();
  const isGeneric = templateType === "GENERIC";
  const isButton = templateType === "BUTTON";
  const isMedia = templateType === "MEDIA";

  const renderButtons = (buttons: FacebookButton[] | undefined) => {
    if (!buttons || buttons.length === 0) return null;

    return (
      <div className={styles.templatePreviewButtons}>
        {buttons.slice(0, 3).map((btn: FacebookButton, idx: number) => (
          <div key={idx} className={styles.templatePreviewButton}>
            {btn.type === "web_url" ? (
              <LinkOutlined style={{ fontSize: 12 }} />
            ) : (
              <SendOutlined style={{ fontSize: 12 }} />
            )}
            <span>{btn.title || "Button"}</span>
          </div>
        ))}
      </div>
    );
  };

  const handleEditTemplate = () => {
    navigate(ROUTES.SETTINGS.FACEBOOK_TEMPLATE_EDIT(Number(template.id)));
  };

  if (isGeneric) {
    const title = payload.title;
    const subtitle = payload.subtitle;
    const imageUrl = payload.image_url || payload.imageUrl;
    const buttons = payload.buttons;
    const elements = payload.elements;

    const firstElement =
      Array.isArray(elements) && elements.length > 0 ? elements[0] : null;
    const displayTitle = title || firstElement?.title;
    const displaySubtitle = subtitle || firstElement?.subtitle;
    const displayImageUrl =
      imageUrl || firstElement?.image_url || firstElement?.imageUrl;
    const displayButtons = buttons || firstElement?.buttons;

    return (
      <div className={styles.templatePreview}>
        {displayImageUrl ? (
          <img
            src={displayImageUrl}
            alt="preview"
            className={styles.templatePreviewImage}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className={styles.templatePreviewImagePlaceholder}>
            <PictureOutlined style={{ fontSize: 20, color: "#fff" }} />
          </div>
        )}
        <div className={styles.templatePreviewContent}>
          {displayTitle && (
            <div className={styles.templatePreviewTitle}>{displayTitle}</div>
          )}
          {displaySubtitle && (
            <div className={styles.templatePreviewSubtitle}>
              {displaySubtitle}
            </div>
          )}
        </div>
        {renderButtons(displayButtons)}
        {(onSend || onCancel) && (
          <div className={styles.templatePreviewActions}>
            <Button
              type="text"
              size="small"
              onClick={handleEditTemplate}
              danger
            >
              Chỉnh sửa
            </Button>
            {onCancel && (
              <Button size="small" type="text" onClick={handleCancel}>
                Hủy
              </Button>
            )}
            {onSend && (
              <Button type="primary" size="small" onClick={handleSend}>
                Gửi
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (isButton) {
    const text = payload.text;
    const buttons = payload.buttons;

    return (
      <div className={styles.templatePreview}>
        {text && <div className={styles.templatePreviewBubble}>{text}</div>}
        {buttons && buttons.length > 0 && (
          <div className={styles.templatePreviewButtonList}>
            {buttons.slice(0, 3).map((btn: FacebookButton, idx: number) => (
              <div key={idx} className={styles.templatePreviewButtonOutlined}>
                {btn.type === "web_url" ? (
                  <LinkOutlined style={{ fontSize: 12 }} />
                ) : (
                  <SendOutlined style={{ fontSize: 12 }} />
                )}
                <span>{btn.title || "Button"}</span>
              </div>
            ))}
          </div>
        )}
        {(onSend || onCancel) && (
          <div className={styles.templatePreviewActions}>
            <Button
              type="text"
              size="small"
              onClick={handleEditTemplate}
              danger
            >
              Chỉnh sửa
            </Button>
            {onCancel && (
              <Button size="small" type="text" onClick={handleCancel}>
                Hủy
              </Button>
            )}
            {onSend && (
              <Button type="primary" size="small" onClick={handleSend}>
                Gửi
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (isMedia) {
    const mediaType = payload.media_type || "image";
    const mediaUrl = payload.media_url || payload.mediaUrl;
    const buttons = payload.buttons;
    const aspectRatio = payload.aspect_ratio || "horizontal";
    const height = aspectRatio === "square" ? 120 : 120;

    return (
      <div className={styles.templatePreview}>
        {mediaType === "video" ? (
          <div className={styles.templatePreviewVideo} style={{ height }}>
            {mediaUrl ? (
              <video
                src={mediaUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: "120px",
                  objectFit: "cover",
                }}
                controls={false}
              />
            ) : (
              <>
                <PlayCircleOutlined style={{ fontSize: 24, color: "#fff" }} />
              </>
            )}
            <div className={styles.templatePreviewPlayOverlay}>
              <PlayCircleOutlined style={{ fontSize: 16, color: "#fff" }} />
            </div>
          </div>
        ) : mediaUrl ? (
          <img
            src={mediaUrl}
            alt="preview"
            className={styles.templatePreviewImage}
            style={{ height }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div
            className={styles.templatePreviewImagePlaceholder}
            style={{ height }}
          >
            <PictureOutlined style={{ fontSize: 20, color: "#fff" }} />
          </div>
        )}
        {renderButtons(buttons)}
        {(onSend || onCancel) && (
          <div className={styles.templatePreviewActions}>
            <Button
              type="text"
              size="small"
              onClick={handleEditTemplate}
              danger
            >
              Chỉnh sửa
            </Button>
            {onCancel && (
              <Button size="small" type="text" onClick={handleCancel}>
                Hủy
              </Button>
            )}
            {onSend && (
              <Button type="primary" size="small" onClick={handleSend}>
                Gửi
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};
